import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Star, ShieldCheck, ShieldQuestion, MessageSquare, Clock, Camera, Loader2 } from "lucide-react";

const CRITERIA = [
  { key: "accueil", label: "Accueil" },
  { key: "qualite_prix", label: "Qualité / prix" },
  { key: "reactivite", label: "Réactivité" },
];

const Stars = ({ value, size = 16 }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={size} className={i <= Math.round(value) ? "text-[#F47B20] fill-[#F47B20]" : "text-slate-300"} />
    ))}
  </span>
);

const StarPick = ({ value, onPick, testId }) => (
  <span className="inline-flex items-center gap-0.5" data-testid={testId}>
    {[1, 2, 3, 4, 5].map((i) => (
      <button key={i} type="button" onClick={() => onPick(i)} className="p-0.5">
        <Star size={22} className={i <= value ? "text-[#F47B20] fill-[#F47B20]" : "text-slate-300 hover:text-[#F47B20]"} />
      </button>
    ))}
  </span>
);

export const BusinessReviews = ({ businessId, businessName }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [mine, setMine] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [criteria, setCriteria] = useState({});
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => {
    api.get(`/businesses/${businessId}/reviews`).then((r) => setData(r.data)).catch(() => setData({ items: [], summary: { average: 0, count: 0, criteria_averages: {} } }));
    if (user && user !== false) {
      api.get(`/businesses/${businessId}/reviews/me`).then((r) => {
        setMine(r.data);
        if (r.data.review) {
          setRating(r.data.review.rating); setComment(r.data.review.comment || "");
          setCriteria(r.data.review.criteria || {}); setPhotos(r.data.review.photos || []);
        }
      }).catch(() => setMine(null));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [businessId, user]);

  // Lien profond « #avis » (invitation après devis réalisé) : on ouvre et on cible le formulaire
  useEffect(() => {
    if (data && typeof window !== "undefined" && window.location.hash === "#avis") {
      const el = document.getElementById("avis");
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  }, [data]);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data: d } = await api.post("/entre-voisins/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (d?.url) urls.push(d.url);
      }
      setPhotos((p) => [...p, ...urls].slice(0, 6));
    } catch { toast.error("Envoi de photo impossible."); } finally { setUploading(false); }
  };

  const submit = async () => {
    if (!rating) { toast.error("Choisissez une note de 1 à 5 étoiles."); return; }
    setBusy(true);
    try {
      await api.post(`/businesses/${businessId}/reviews`, { rating, comment, criteria, photos });
      toast.success("Merci ! Votre avis sera publié après validation.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi impossible.");
    } finally { setBusy(false); }
  };

  if (!data) return null;
  const { items, summary } = data;
  const critAvg = summary.criteria_averages || {};

  return (
    <section id="avis" className="mt-10 scroll-mt-24" data-testid="business-reviews">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="font-heading text-xl font-bold">Avis clients</h3>
          <div className="mt-1 flex items-center gap-2 text-sm text-slate-600" data-testid="business-rating-summary">
            <Stars value={summary.average} />
            <span className="font-bold text-slate-900">{summary.average || "—"}</span>
            <span className="text-slate-500">({summary.count} avis)</span>
          </div>
          {Object.keys(critAvg).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500" data-testid="criteria-averages">
              {CRITERIA.filter((c) => critAvg[c.key]).map((c) => (
                <span key={c.key}>{c.label} <strong className="text-slate-700">{critAvg[c.key]}</strong>/5</span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Formulaire — ouvert à tout membre connecté */}
      {user && user !== false ? (
        <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-4" data-testid="review-form">
          <div className={`flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest ${mine?.verified_eligible ? "text-emerald-700" : "text-slate-500"}`}>
            {mine?.verified_eligible ? <><ShieldCheck size={12} /> Votre avis sera « Vérifié » (expérience confirmée)</> : <><ShieldQuestion size={12} /> Avis « Non vérifié » — expérience non confirmée</>}
          </div>
          <div className="mt-2 font-heading font-bold text-slate-900">{mine?.review ? "Modifier mon avis" : `Noter ${businessName}`}</div>
          {mine?.review?.status === "pending" && (
            <div className="mt-1 inline-flex items-center gap-1 text-xs text-amber-700" data-testid="review-pending-badge"><Clock size={12} /> En attente de validation.</div>
          )}
          <div className="mt-3"><StarPick value={rating} onPick={setRating} testId="review-stars-input" /></div>

          <div className="mt-3 grid sm:grid-cols-3 gap-2" data-testid="review-criteria">
            {CRITERIA.map((c) => (
              <div key={c.key} className="flex items-center justify-between gap-2 bg-slate-50 rounded-lg px-2.5 py-1.5">
                <span className="text-xs text-slate-600">{c.label}</span>
                <StarPick value={criteria[c.key] || 0} onPick={(v) => setCriteria((p) => ({ ...p, [c.key]: v }))} testId={`review-crit-${c.key}`} />
              </div>
            ))}
          </div>

          <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Votre expérience en quelques mots (optionnel)" className="mt-3" data-testid="review-comment-input" />

          <label className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-slate-300 text-sm text-slate-500 cursor-pointer hover:border-[#F47B20] w-fit" data-testid="review-photos">
            {uploading ? <Loader2 size={15} className="animate-spin" /> : <Camera size={15} />} Ajouter des photos
            <input type="file" accept="image/*" multiple className="hidden" onChange={onUpload} />
          </label>
          {photos.length > 0 && <div className="flex gap-2 mt-2 flex-wrap">{photos.map((u, i) => <img key={i} src={u} alt="" className="w-16 h-16 rounded-lg object-cover border" />)}</div>}

          <div className="mt-3 flex justify-end">
            <Button onClick={submit} disabled={busy} className="btn-shine text-white" data-testid="review-submit">
              {busy ? "Envoi…" : mine?.review ? "Mettre à jour mon avis" : "Publier mon avis"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-5 rounded-2xl border border-dashed border-slate-300 bg-white p-4 text-sm text-slate-600" data-testid="review-login-prompt">
          Connectez-vous pour laisser un avis sur {businessName}.
        </div>
      )}

      {/* Liste */}
      <div className="mt-6 space-y-3" data-testid="reviews-list">
        {items.length === 0 ? (
          <div className="text-sm text-slate-500" data-testid="reviews-empty">Aucun avis publié pour le moment.</div>
        ) : items.map((r) => (
          <div key={r.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`review-${r.id}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <Stars value={r.rating} size={14} />
                <span className="font-semibold text-slate-900 text-sm">{r.author}</span>
                {r.verification_status === "verified" ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"><ShieldCheck size={10} /> Vérifié</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5"><ShieldQuestion size={10} /> Non vérifié</span>
                )}
              </div>
              <span className="text-[11px] text-slate-400">{(r.created_at || "").slice(0, 10)}</span>
            </div>
            {r.comment && <p className="mt-2 text-sm text-slate-700 whitespace-pre-line">{r.comment}</p>}
            {r.photos?.length > 0 && <div className="flex gap-2 mt-2 flex-wrap">{r.photos.map((u, i) => <img key={i} src={u} alt="" className="w-20 h-20 rounded-lg object-cover border" />)}</div>}
            {r.pro_reply && (
              <div className="mt-3 rounded-xl bg-slate-50 border border-slate-200 p-3">
                <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500"><MessageSquare size={11} /> Réponse de {businessName}</div>
                <p className="mt-1 text-sm text-slate-700 whitespace-pre-line">{r.pro_reply}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

export default BusinessReviews;
